import {connect} from 'react-redux';
import {Component, Fragment} from 'react';
import {formatPhone} from '@deliveriesondemand/dod-utils';

import BuyerInfoService from '-/services/BuyerInfoService';
import UIService from '-/services/UIService';
import NavigationService from '-/services/NavigationService';
import ApiService from '-/services/ApiService';
import CreditorService from '-/services/CreditorService';
import DealInfoService from '-/services/DealInfoService';
import SubmitButton from '-/components/general/SubmitButton';
import Disclosure from '-/components/general/Disclosure';
import SSNInput from '-/components/fields/SSNInput';
import Form from '-/components/fields/Form';
import Legend from '-/components/fields/Legend';
import Fieldset from '-/components/fields/Fieldset';
import Checkbox from '-/components/fields/Checkbox';
import OptInMessage from '-/components/general/OptInMessage';
import {get} from '-/util/lodash';
import {Control, ControlGroup} from '-/components/fields/Control';
import TextInput from '-/components/fields/TextInput';
import {checkReferral} from '-/api/api';
import {getOfferId} from '-/util/util';
import {renderFieldErrors} from '-/util/util';

class Submit extends Component {
    constructor(props) {
        super(props);

        this.state = {
            appSSNPart: '',
            coAppSSNPart: '',
            referralCode: '',
            hasReferralError: false,
            submitting: false,
            cellphoneOptIn: get(props, 'buyerInfo.cellphoneOptIn'),
            cellphoneOptIn_CoApp: get(props, 'buyerInfo_CoApp.cellphoneOptIn'),
            agreed: false
        };
    }

    handleSSNPartChange = (part, value) => {
        this.setState({
            [part]: value
        });
    };

    handleOptInCheckboxChange = ({checked}) => {
        BuyerInfoService.set('cellphoneOptIn', checked);
    };

    handleCorrectClick = () => {
        const {allowReferrals, buyerInfo, buyerInfo_CoApp, coAppUnlocked, collectAppSSN, collectCoAppSSN, isCashDeal, offerOrigin} = this.props;
        const {ssn: appSSN, name: appName} = buyerInfo;
        const {ssn: coAppSSN, ssnComplete: coAppSSNComplete, name: coAppName} = buyerInfo_CoApp;
        const {appSSNPart, coAppSSNPart, submitting, agreed, hasReferralError, referralCode} = this.state;

        if (!submitting) {
            if (!agreed) {
                alert('Please agree to the terms and conditions.');
            } else if (collectAppSSN && (!appSSNPart || appSSNPart.length != 2)) {
                alert(`${appName}: Please enter the first two digits of your SSN`);
            } else if (collectCoAppSSN && (!coAppSSNPart || coAppSSNPart.length != 2)) {
                alert(`${coAppName}: Please enter the first two digits of your SSN`);
            } else if (!isCashDeal && offerOrigin === 'NEW' && ((!coAppSSN && coAppUnlocked && !coAppSSNComplete) || !appSSN)) {
                alert('Please return to the Applicant Info section and provide a SSN');
            } else {
                if (appSSNPart || coAppSSNPart) {
                    ApiService.saveSSN({
                        ...appSSNPart && {app: appSSNPart},
                        ...coAppSSNPart && {coApp: coAppSSNPart}
                    });
                }

                this.setState({submitting: true});

                const data = {};

                if (allowReferrals && referralCode.length === 6 && !hasReferralError) {
                    data.referralCode = referralCode;
                }

                ApiService.submitFinal(data).then(res => {
                    const ltvPass = get(res, 'data.ltvPass');

                    setTimeout(() => {
                        UIService.stepCompleted('finalReview');
                        NavigationService.next('congrats', {ltvPass});
                    }, 1000);
                }).catch(() => this.setState({submitting: false}));
            }
        }
    };

    handleKeyDown = e => {
        if (e.keyCode === 13) {
            e.preventDefault();
            this.handleCorrectClick();
        }
    };

    handleAgreeCheckboxChange = ({checked: agreed}) => {
        this.setState({
            agreed
        });
    };

    onReferralCodeChange = value => {
        const code = value.toUpperCase();
        const offerId = getOfferId();
        const referralCode = code.substring(0, 6);

        this.setState({referralCode});

        if (referralCode.length === 6) {
            checkReferral({code: referralCode, _id: offerId})
                .then(res => {
                    const {data} = res;
                    const {isValid} = data;

                    this.setState({hasReferralError: !isValid});
                })
                .catch(() => {
                    this.setState({hasReferralError: true});
                });
        }
    }

    render() {
        const {
            allowReferrals,
            buyerInfo: {
                lastFour: appLastFour,
                name,
                cellphone
            },
            buyerInfo_CoApp: {
                lastFour: coAppLastFour,
                name: name_CoApp,
                cellphone: cellphone_CoApp
            },
            collectAppSSN,
            collectCoAppSSN,
            hasReferral,
            isCU,
            isDealer
        } = this.props;
        const {appSSNPart, coAppSSNPart, submitting, cellphoneOptIn, cellphoneOptIn_CoApp, hasReferralError, referralCode} = this.state;
        const creditorName = CreditorService.getName();
        const baseOptInMessageProps = {
            template: CreditorService.getOptInMessage(),
            creditorName
        };
        const optInMessageProps = {
            ...baseOptInMessageProps,
            name,
            cellphone: formatPhone(cellphone)
        };
        const optInMessageProps_CoApp = {
            ...baseOptInMessageProps,
            name: name_CoApp,
            cellphone: formatPhone(cellphone_CoApp)
        };
        const status = UIService.getStatus();
        const isSubmitted = status === 'SUBMITTED';
        const authorizedName = isDealer ? creditorName : 'the Credit Union';

        return (
            <div className="submit-step">
                {isSubmitted && 
                    <div style={{margin: '1.875rem', marginTop: '3rem'}}>
                        <Fieldset type="flow">
                            <Legend type="flow">Application Submitted!</Legend>
                            <p>Thanks for submitting your application with us!</p>
                        </Fieldset>
                    </div>
                }
                {!isSubmitted && 
                    <Form onKeyDown={this.handleKeyDown} standalone={true}>
                        <Fieldset type="flow">
                            <Legend type="flow">Great Work!</Legend>
                            {isCU && <p className="info">You are about to realize your savings and get your new low rate.</p>}
                            <p className="info">There are a bunch of terms and conditions below for you to review. Like all terms and conditions, they are important. You can read all of the finer details by clicking &quot;Important Legal Stuff&quot; below.</p>
                            <Disclosure title="Important Legal Stuff">
                                <p className="info">Depending on the nature of this request, these disclosures may contain important information regarding applicable rates, fees and other costs.</p>
                                <p className="info">By checking the box below and submitting this request, all parties named below are agreeing to the terms, conditions and disclosures associated with this request.</p>
                                <p className="info">Note: A confirmation email will be sent to the email address provided confirming we received your loan request.</p>
                                <p className="info">Credit Authorization: By submitting this request, you authorize {authorizedName} to obtain credit reports in connection with this request for credit and for any update, increase, renewal, extension, or collection of the credit received. You understand that {authorizedName} will rely on information in this request and your credit report to make its decision. When applicable, spousal information may be requested as a part of this request. If you request, {authorizedName} will tell you the name and address of any credit bureau from which it received a credit report on you. It is a crime to willfully and deliberately provide incomplete or incorrect information in this request.</p>
                            </Disclosure>
                            <p className="info">
                                <Checkbox handleCheckboxChange={this.handleAgreeCheckboxChange}>
                                    I, {name}, agree to the terms, conditions and disclosures of this request as the Primary Applicant.
                                </Checkbox>
                            </p>
                            {cellphone && !cellphoneOptIn && (
                                <div className="info">
                                    <OptInMessage {...optInMessageProps} onChange={this.handleOptInCheckboxChange} />
                                </div>
                            )}
                            {cellphone_CoApp && !cellphoneOptIn_CoApp && (
                                <div className="info">
                                    <OptInMessage {...optInMessageProps_CoApp} onChange={this.handleOptInCheckboxChange} />
                                </div>
                            )}
                            {(collectAppSSN || collectCoAppSSN) &&
                                <div className="info">
                                    <p>To submit your credit app for a complete approval, we will need to have you input the first two characters of your Social Security Number. During the prescreen process, we received the last seven characters. Entering the first two characters means you want to submit this credit app for approval and authorize us to check your credit. Please enter the first two characters of your Social Security Number.</p>
                                    <br />
                                    {collectAppSSN &&
                                        <div className="masked-form">
                                            <h4>{name}</h4>
                                            <div className="control">
                                                <SSNInput
                                                    value={appSSNPart}
                                                    onChange={this.handleSSNPartChange.bind(this, 'appSSNPart')}
                                                    lastFour={appLastFour}
                                                    part="first2"
                                                />
                                            </div>
                                        </div>
                                    }
                                    {collectCoAppSSN &&
                                        <div className="masked-form">
                                            <h4>{name_CoApp}</h4>
                                            <div className="control">
                                                <SSNInput
                                                    value={coAppSSNPart}
                                                    onChange={this.handleSSNPartChange.bind(this, 'coAppSSNPart')}
                                                    lastFour={coAppLastFour}
                                                    part="first2"
                                                />
                                            </div>
                                        </div>
                                    }
                                </div>
                            }
                            {allowReferrals && !hasReferral &&
                                <Fragment>
                                    <p className="info">If you have a referral code, enter it here:</p>
                                    <ControlGroup>
                                        <Control>
                                            <TextInput
                                                label="Referral Code"
                                                placeholder="ABC123"
                                                value={referralCode}
                                                onChange={this.onReferralCodeChange} />
                                        </Control>
                                        {hasReferralError && 
                                            <div style={{marginTop: 5}}>
                                                {renderFieldErrors(['The referral code you entered is not valid. Please check the code and try again'])}
                                            </div>
                                        }
                                    </ControlGroup>
                                </Fragment>
                            }
                            <SubmitButton disabled={submitting} onClick={this.handleCorrectClick} />
                        </Fieldset>
                    </Form>
                }
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {config = {}, navigation = {}} = state;
    const {hasReferral, origin = ''} = config;
    const isCashDeal = DealInfoService.isCashDeal();
    const appLastFour = get(state, 'buyerInfo.buyerInfo.lastFour');
    const coAppLastFour = get(state, 'buyerInfo.buyerInfo_CoApp.lastFour');
    const collectAppSSN = !!appLastFour && !isCashDeal;
    const collectCoAppSSN = !!coAppLastFour && !isCashDeal;
    const {coAppUnlocked} = navigation;
    const dealType = get(state, 'creditor.type');
    const allowReferrals = get(state, 'creditor.allowReferrals');

    return {
        ...BuyerInfoService.getReducerState(),
        allowReferrals,
        coAppUnlocked,
        collectAppSSN,
        collectCoAppSSN,
        hasReferral,
        isCashDeal,
        isCU: dealType === 'creditUnion',
        isDealer: dealType === 'dealer',
        offerOrigin: origin
    };
};

export default connect(mapStateToProps)(Submit);
